import type { EChartsOption } from 'echarts';

import { type Cadence, type ChartData, type Pane, formatValue, periodLabel } from './model';

// Fixed hues for the workbook's known categories; other series (BDM names) take the palette in name order.
const palette = [
  '#0F464F',
  '#35B88B',
  '#447AB3',
  '#C18728',
  '#C96C5A',
  '#8B70AF',
  '#6D9443',
  '#428E98',
  '#B66D91',
  '#8B7964',
  '#677FB3',
  '#AD9651',
];
const fixed: Record<string, string> = {
  'New Agent': '#35B88B',
  Existing: '#0F464F',
  'Booked in period': '#35B88B',
  'Not booked in period': '#C96C5A',
  'Signed in Period': '#447AB3',
  'Active - Interacted': '#35B88B',
  'Active - Not Interacted': '#C18728',
  Inactive: '#8B7964',
  '0': '#C96C5A',
  '1': '#C18728',
  '2': '#447AB3',
  '3+': '#0F464F',
  'Total bookings': '#8B70AF',
  'Agency bookings': '#8B70AF',
};
function seriesColors(names: ReadonlyArray<string>, dark = false): Map<string, string> {
  const open = [...names].filter((name) => !fixed[name]).sort((a, b) => a.localeCompare(b));
  return new Map(
    names.map((name) => {
      const color = fixed[name] ?? palette[open.indexOf(name) % palette.length];
      return [name, dark && color === '#0F464F' ? '#4EFEB9' : color];
    }),
  );
}

const axisText = (value: number, format: Pane['format']) =>
  format === 'percent'
    ? `${Math.round(value * 100)}%`
    : format === 'decimal'
      ? value.toFixed(0)
      : value.toLocaleString('en-AU');

export function buildChartOption(data: ChartData, cadence: Cadence, dark: boolean, height: number): EChartsOption {
  const text = dark ? '#E8E4DA' : '#292B29';
  const grid = dark ? '#FFFFFF20' : '#0F0C081A';
  const legendHeight = 28;
  const paneHeight = height - legendHeight - 36;
  const names = [...new Set(data.panes.flatMap((pane) => pane.series.map((series) => series.name)))];
  const colors = seriesColors(names, dark);
  const labels = data.categories.map((period) => periodLabel(period, cadence));
  return {
    animation: false,
    backgroundColor: 'transparent',
    textStyle: { fontFamily: 'Inter, Arial, sans-serif', color: text },
    aria: { enabled: true, decal: { show: true } },
    legend: {
      type: 'scroll',
      bottom: 0,
      left: 8,
      right: 8,
      data: names,
      textStyle: { color: text, fontSize: 11 },
      pageTextStyle: { color: text },
      itemWidth: 14,
      itemHeight: 9,
    },
    tooltip: {
      trigger: 'axis',
      confine: true,
      renderMode: 'richText',
      backgroundColor: dark ? '#25221E' : '#FFFFFF',
      borderColor: grid,
      textStyle: { color: text, fontSize: 12 },
    },
    axisPointer: { link: [{ xAxisIndex: 'all' }] },
    grid: [{
      left: 8,
      right: 8,
      top: 24,
      height: paneHeight,
      containLabel: true,
    }],
    xAxis: [{
      type: 'category' as const,
      data: labels,
      axisLine: { lineStyle: { color: grid } },
      axisTick: { show: false },
      axisLabel: { color: text, hideOverlap: true },
    }],
    yAxis: data.panes.map((pane, index) => ({
      type: 'value' as const,
      position: index === 0 ? 'left' as const : 'right' as const,
      name: pane.title === 'Bookings per 100 interactions' ? 'Bookings / 100' : pane.title.startsWith('% agencies converted') ? '% converted' : pane.title,
      nameTextStyle: { color: text, align: index === 0 ? 'left' as const : 'right' as const, fontSize: 11 },
      axisLabel: { color: text, formatter: (value: number) => axisText(value, pane.format) },
      splitLine: { show: index === 0, lineStyle: { color: grid } },
      ...(pane.format === 'percent' && pane.stacked ? { min: 0, max: 1 } : {}),
    })),
    series: data.panes.flatMap((pane, index) =>
      pane.series.map((series) => ({
        name: series.name,
        type: index === 0 ? 'bar' as const : 'line' as const,
        yAxisIndex: index,
        barMaxWidth: 36,
        ...(pane.stacked ? { stack: `pane-${index}` } : {}),
        itemStyle: { color: colors.get(series.name) },
        emphasis: { focus: 'series' as const },
        tooltip: { valueFormatter: (value: unknown) => formatValue(value == null ? null : Number(value), pane.format) },
        data: series.values,
      })),
    ),
  };
}
